import { Landmark, TrainFront, CarFront, Video, Sparkles, RefreshCw, ShieldCheck } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { IMAGES } from "../data/site";

const STUDIO_POINTS = [
  { icon: Landmark, text: "Di fronte al Ponte di Ferro che collega Garbatella" },
  { icon: TrainFront, text: "A 5 minuti a piedi dalla metro" },
  { icon: CarFront, text: "Ampio parcheggio proprio di fronte allo studio" },
];

const ONLINE_POINTS = [
  { icon: Sparkles, title: "Per iniziare un nuovo percorso", text: "Il primo colloquio e l'intero percorso possono svolgersi interamente online, con la stessa cura e profondità della seduta in presenza." },
  { icon: RefreshCw, title: "Per proseguire un percorso già iniziato", text: "Trasferimenti, impegni o periodi intensi non interrompono il lavoro: gli incontri continuano online, anche alternando studio e videochiamata." },
  { icon: ShieldCheck, title: "Riservata e sicura", text: "Utilizzo piattaforme protette e conformi alla privacy: ciò che condividi resta tutelato esattamente come in studio." },
];

export const StudioOnline = () => (
  <section id="studio" className="py-20 lg:py-32 relative grain-overlay overflow-hidden" data-testid="studio-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Chapter number="05" label="Dove ci incontriamo" />
      <Reveal delay={0.08}>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] mt-5 text-[#1C1E1D] max-w-2xl">
          In studio a Roma Ostiense, <em className="text-[#4A6B5D] not-italic">oppure online</em>
        </h2>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
        <Reveal delay={0.1} className="h-full">
          <article className="h-full bg-[#FAF7F0] border border-[#1C1E1D]/8 rounded-2xl overflow-hidden flex flex-col hover:shadow-[0_20px_50px_-15px_rgba(28,30,29,0.12)] transition-shadow duration-500" data-testid="studio-card">
            <div className="overflow-hidden">
              <img src={IMAGES.cozyInterior} alt="Lo studio in Via Ostiense 104B"
                   className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700" data-testid="studio-card-image" />
            </div>
            <div className="p-8 sm:p-10 flex-1 flex flex-col">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#C85A32]">In Presenza</p>
              <h3 className="font-serif text-2xl font-medium mt-3 text-[#1C1E1D]">Lo studio in Via Ostiense 104B</h3>
              <p className="mt-4 text-sm text-[#525754] leading-relaxed">
                Ricevo nel mio studio a Roma Ostiense, in Via Ostiense 104B, in uno spazio pensato per offrire
                privacy, tranquillità e la possibilità di sentirsi a proprio agio fin dai primi incontri.
              </p>
              <p className="mt-3 text-sm text-[#525754] leading-relaxed">
                Per me il luogo della terapia non è un dettaglio. Entrare in uno spazio nel quale ci si possa
                fermare, senza fretta e senza il rumore della quotidianità, aiuta a creare quella continuità e
                quella sicurezza necessarie per poter parlare anche di ciò che normalmente è più difficile mostrare.
              </p>
              <ul className="mt-7 space-y-4">
                {STUDIO_POINTS.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <li key={i} className="flex items-center gap-3 text-sm text-[#1C1E1D]">
                      <span className="w-9 h-9 rounded-full bg-[#F3EFE6] border border-[#1C1E1D]/10 flex items-center justify-center text-[#4A6B5D] shrink-0">
                        <Icon size={15} strokeWidth={1.8} />
                      </span>
                      {p.text}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-sm text-[#525754] leading-relaxed">
                La zona è facilmente raggiungibile e permette di integrare il percorso terapeutico anche
                all'interno di giornate lavorative o universitarie.
              </p>
              <div className="mt-7 pt-6 border-t border-[#1C1E1D]/10 flex-1" data-testid="studio-first-meetings">
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#C85A32]">I primi colloqui</p>
                <div className="mt-4 space-y-3 text-sm text-[#525754] leading-relaxed">
                  <p>
                    Durante i primi colloqui ci prendiamo il tempo necessario per comprendere cosa ti sta
                    portando a chiedere un supporto, quali sono le difficoltà che stai vivendo e che tipo di
                    percorso potrebbe esserti più utile.
                  </p>
                  <p>
                    Non è necessario arrivare con tutto già chiaro. A volte si parte da un sintomo preciso,
                    altre volte da una sensazione più difficile da definire: sentirsi bloccati, confusi,
                    insoddisfatti o accorgersi che alcune dinamiche continuano a ripetersi.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </Reveal>

        <Reveal delay={0.18} className="h-full">
          <article className="h-full bg-[#1C211E] rounded-2xl overflow-hidden flex flex-col" data-testid="online-card">
            <div className="overflow-hidden">
              <img src={IMAGES.online} alt="Seduta online: le mani sulla tastiera durante una videochiamata"
                   className="w-full h-52 object-cover hover:scale-105 transition-transform duration-700" data-testid="online-card-image" />
            </div>
            <div className="p-8 sm:p-10 flex-1 flex flex-col">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4A359]">A Distanza</p>
            <h3 className="font-serif text-2xl font-medium mt-3 text-[#F5F3ED] inline-flex items-center gap-3">
              Psicoterapia online
              <span className="w-9 h-9 rounded-full bg-[#F5F3ED]/10 flex items-center justify-center text-[#D4A359]"><Video size={16} /></span>
            </h3>
            <p className="mt-4 text-sm text-[#F5F3ED]/65 leading-relaxed">
              Gli incontri online permettono di iniziare o proseguire un percorso anche quando la
              distanza o gli impegni rendono difficile raggiungere lo studio. L'efficacia è
              comparabile a quella delle sedute in presenza.
            </p>
            <div className="mt-8 space-y-6 flex-1">
              {ONLINE_POINTS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="flex gap-4">
                    <span className="w-9 h-9 rounded-full bg-[#F5F3ED]/8 border border-[#F5F3ED]/12 flex items-center justify-center text-[#D4A359] shrink-0">
                      <Icon size={15} strokeWidth={1.8} />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-[#F5F3ED]">{p.title}</p>
                      <p className="text-sm text-[#F5F3ED]/55 leading-relaxed mt-1">{p.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 pt-7 border-t border-[#F5F3ED]/10" data-testid="online-how-it-works">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4A359]">Come funziona</p>
              <div className="mt-4 space-y-3 text-sm text-[#F5F3ED]/65 leading-relaxed">
                <p>Le sedute si svolgono in videochiamata, in un momento concordato insieme.</p>
                <p>Per vivere bene l'incontro è importante avere a disposizione un luogo sufficientemente tranquillo e riservato, nel quale poter parlare senza interruzioni e sentirsi liberi di affrontare anche argomenti personali.</p>
                <p>Il percorso, esattamente come in studio, parte dai primi colloqui: comprendiamo insieme cosa stai vivendo, cosa desideri cambiare o approfondire e quale lavoro può essere più adatto a te.</p>
              </div>
            </div>
            </div>
          </article>
        </Reveal>
      </div>
    </div>
  </section>
);
